"use client";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import CalendarIcon from "@untitled-ui/icons-react/build/esm/Calendar";
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";

const { Text } = Typography;

const BondFeaturesForm = (props) => {
  const {
    onFinish,
    form,
    couponRate,
    initialValues,
    issuePriceOptions,
    couponRateOptions,
    floatingRateOptions,
    couponFrequencyOptions,
    ammortizationOptions,
    checkedRepaymentType,
    checkedCallable,
    checkedPutable,
    checkedConvertible,
    setCheckedCallable,
    setCheckedPutable,
    setCheckedConvertible,
    setCheckedRepaymentType,
    setFloatingSearchInput,
    currencySymbol,
  } = props;

  const handleCallableChange = (checked) => {
    setCheckedCallable(checked);
  };

  const handlePutableChange = (checked) => {
    setCheckedPutable(checked);
  };

  const handleConvertibleChange = (checked) => {
    setCheckedConvertible(checked);
  };

  const handleRepaymentTypeChange = (checked) => {
    setCheckedRepaymentType(checked);
  };

  const today = dayjs();

  useEffect(() => {
    if (couponRate === 0) {
      form.setFieldsValue({ floatingCouponRate: undefined });
    } else if (couponRate === 1) {
      form.setFieldsValue({ fixedCouponRate: undefined });
    }
  }, [couponRate]);

  return (
    <div>
      <h1
        style={{
          fontSize: "24px",
          fontWeight: "bold",
          margin: 0,
          marginBottom: "24px",
        }}
      >
        Bond Features
      </h1>

      <Form
        className="custom-form"
        layout="vertical"
        onFinish={onFinish}
        form={form}
        initialValues={initialValues}
        style={{ width: "100%" }}
      >
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Par Value/Cost Per Unit
                  <Tooltip title="Par Value/Cost Per Unit is the nominal amount that the bond issuer agrees to pay the bondholder at maturity.">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="faceValue"
              rules={[
                {
                  required: true,
                  message: "Par Value is required",
                },
              ]}
            >
              <InputNumber
                className="bond-input tenor-input"
                style={{ height: "44px", width: "100%" }}
                placeholder="e.g., 1,000"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => (value ? value.replace(/(,*)/g, "") : "")}
                prefix={currencySymbol}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Issue Price
                  <Tooltip title="Select the issue price">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="issuePrice"
              rules={[
                {
                  required: true,
                  message: "Please select issue price",
                },
              ]}
            >
              <Select
                placeholder="At par / discount / premium"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={issuePriceOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Coupon Rate Option
                  <Tooltip title="Select the coupon rate">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="couponRate"
              rules={[
                {
                  required: true,
                  message: "Please select coupon rate",
                },
              ]}
            >
              <Select
                placeholder="Fixed / Floating"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={couponRateOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Coupon Frequency
                  <Tooltip title="Select the coupon frequency">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="couponFrequency"
              rules={[
                {
                  required: true,
                  message: "Please select coupon frequency",
                },
              ]}
            >
              <Select
                placeholder="Select coupon frequency"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={couponFrequencyOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Coupon Rate (Fixed)
                  <Tooltip title="Fixed coupon rate">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="fixedCouponRate"
              // rules={
              //   couponRate === 0
              //     ? [
              //         {
              //           required: true,
              //           message: "Fixed coupon rate is required",
              //         },
              //       ]
              //     : []
              // }
            >
              <InputNumber
                style={{ height: 44, width: "100%" }}
                className="tenor-input"
                placeholder="0"
                suffix={"%"}
                disabled={couponRate !== 0}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Coupon Rate (Floating)
                  <Tooltip title="Floating coupon rate">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="floatingCouponRate"
              // rules={
              //   couponRate === 1
              //     ? [
              //         {
              //           required: true,
              //           message: "Floating coupon rate is required",
              //         },
              //       ]
              //     : []
              // }
            >
              <Select
                showSearch
                placeholder="Search floating rate by name"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                disabled={couponRate !== 1}
                options={floatingRateOptions}
                onSearch={(value) => setFloatingSearchInput(value)}
                filterOption={false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Maturity Date
                  <Tooltip title="The bond maturity date is the specific date when the issuer is obligated to repay the bond’s principal (face value) to the investor">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="maturityDate"
              rules={[
                {
                  required: true,
                  message: "Maturity date is required",
                },
                () => ({
                  validator(_, value) {
                    if (value?.isBefore(today, "day")) {
                      return Promise.reject(
                        new Error(
                          `Maturity date cannot be lesser than ${today.format(
                            "YYYY-MM-DD"
                          )} (Today)`
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker
                className="bond-date-picker"
                style={{
                  width: "100%",
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 11px",
                  fontSize: 14,
                }}
                suffixIcon={<CalendarIcon color="#667085" width={20} />}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Tenor
                  <Tooltip title="Select tenor">
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "4px",
                        color: "#667085",
                      }}
                    />
                  </Tooltip>
                </span>
              }
              name="tenor"
              rules={[
                {
                  required: true,
                  message: "Tenor is required",
                },
                {
                  pattern: /^\d+$/,
                  message: "Tenor must be a number",
                },
              ]}
            >
              <Input
                className="tenor-input"
                addonAfter="years"
                placeholder="Enter total duration"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row
          style={{
            marginTop: 20,
            marginBottom: 20,
          }}
        >
          <Switch
            onClick={props.onSwitchClick}
            checked={checkedCallable}
            onChange={handleCallableChange}
            style={{
              backgroundColor: checkedCallable ? "#b026ff" : "#F2F4F7",
            }}
          />
          <div style={{ width: "60%" }}>
            <Text style={{ marginLeft: 10 }}>Callable</Text>
            <span>
              <Tooltip title="A callable bond gives the issuer the right (but not the obligation) to redeem the bond before its maturity date">
                <InfoCircleOutlined
                  style={{
                    marginLeft: "4px",
                    color: "#667085",
                  }}
                />
              </Tooltip>
            </span>
          </div>
        </Row>

        <Row
          style={{
            marginTop: 20,
            marginBottom: 20,
          }}
        >
          <Switch
            onClick={props.onSwitchClick}
            checked={checkedPutable}
            onChange={handlePutableChange}
            style={{
              backgroundColor: checkedPutable ? "#b026ff" : "#F2F4F7",
            }}
          />
          <div style={{ width: "60%" }}>
            <Text style={{ marginLeft: 10 }}>Puttable</Text>
            <span>
              <Tooltip title="A puttable bond gives the bondholder (investor) the right—but not the obligation—to demand early repayment of the bond before its maturity date, at a predefined put price">
                <InfoCircleOutlined
                  style={{
                    marginLeft: "4px",
                    color: "#667085",
                  }}
                />
              </Tooltip>
            </span>
          </div>
        </Row>

        <Row
          style={{
            marginTop: 20,
            marginBottom: 20,
          }}
        >
          <Switch
            onClick={props.onSwitchClick}
            checked={checkedConvertible}
            onChange={handleConvertibleChange}
            style={{
              backgroundColor: checkedConvertible ? "#b026ff" : "#F2F4F7",
            }}
          />
          <div style={{ width: "60%" }}>
            <Text style={{ marginLeft: 10 }}>Convertible</Text>
            <span>
              <Tooltip
                title="A convertible bond gives the bondholder the right to convert the
                  bond into a predetermined number of the issuer’s shares."
              >
                <InfoCircleOutlined
                  style={{
                    marginLeft: "4px",
                    color: "#667085",
                  }}
                />
              </Tooltip>
            </span>
          </div>
        </Row>

        <Row>
          <Col span={12}>
            <Row
              style={{
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <div>
                <Switch
                  onClick={props.onSwitchClick}
                  checked={checkedRepaymentType}
                  onChange={handleRepaymentTypeChange}
                  style={{
                    backgroundColor: checkedRepaymentType
                      ? "#b026ff"
                      : "#F2F4F7",
                  }}
                />
                <Text style={{ marginLeft: 10, marginRight: 10 }}>
                  Repayment Type
                </Text>
              </div>

              {checkedRepaymentType && (
                <Form.Item
                  name="repaymentType"
                  style={{ display: "flex", alignItems: "center", margin: 0 }}
                  rules={[
                    {
                      required: true,
                      message: "Please select repayment type",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select repayment type"
                    style={{ height: 44, width: 200 }}
                    suffixIcon={<ChevronDown color="#667085" width={20} />}
                    options={ammortizationOptions}
                  />
                </Form.Item>
              )}
            </Row>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default BondFeaturesForm;
